/**
 * Résolution d'affichage métier commerce (Admin).
 * Ordre : API/objet → catalogue statique → libellé humanisé.
 * Le code technique (TRAITEUR) ne doit jamais être affiché tel quel en UI.
 */
import {
  getCommerceFamily,
  getCommerceMetier,
  normalizeCategorieId,
} from '../config/commerce-categories.js';

/** Couleurs chip par famille — même famille visuelle partout. */
export const FAMILY_CHIP_COLORS = {
  restauration: 'border-orange-200 bg-orange-100 text-orange-800',
  retail: 'border-blue-200 bg-blue-100 text-blue-800',
  beaute: 'border-pink-200 bg-pink-100 text-pink-800',
  reservation_espaces: 'border-green-200 bg-green-100 text-green-800',
  loisirs: 'border-teal-200 bg-teal-100 text-teal-800',
  services_pro: 'border-purple-200 bg-purple-100 text-purple-800',
  autre: 'border-gray-200 bg-gray-100 text-gray-800',
};

const SMALL_WORDS = new Set(['a', 'de', 'du', 'des', 'et', 'la', 'le', 'les', 'en', 'au', 'aux']);

/**
 * TRAITEUR_EVENEMENTIEL → Traiteur événementiel
 * Jamais laisser un code FULL_CAPS tel quel.
 */
export function humanizeMetierId(code) {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  const parts = raw.split(/_+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return parts
    .map((part, index) => {
      const lower = part.toLowerCase();
      if (index > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * True si la chaîne ressemble à un code métier brut (FULL_CAPS / underscores).
 */
export function looksLikeRawMetierCode(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  return /^[A-Z][A-Z0-9_]*$/.test(s);
}

export function getMetierFamilyChipClass(familyId, { primary = false } = {}) {
  if (primary) {
    return 'border-indigo-400 bg-indigo-50 text-indigo-900';
  }
  return FAMILY_CHIP_COLORS[familyId] || FAMILY_CHIP_COLORS.autre;
}

/**
 * @param {string} code
 * @param {{ metier?: object|null, getMetier?: (id: string) => object|null }} [options]
 */
export function resolveMetierDisplay(code, options = {}) {
  const { metier = null, getMetier = null } = options;
  const id = normalizeCategorieId(code);

  if (!id) {
    return {
      id: '',
      label: '—',
      icon: '📦',
      familyId: 'autre',
      badge: null,
      source: 'empty',
      metier: null,
      familyLabel: getCommerceFamily('autre')?.label || 'Autre',
    };
  }

  const fromApi =
    metier ||
    (typeof getMetier === 'function' ? getMetier(id) : null) ||
    null;

  if (fromApi && (fromApi.label || fromApi.id)) {
    const familyId =
      fromApi.familyId || fromApi.family || getCommerceMetier(id)?.family || 'autre';
    const label = String(fromApi.label || '').trim() || humanizeMetierId(id);
    return {
      id,
      label,
      icon: fromApi.icon || '📦',
      familyId,
      badge: fromApi.badge || null,
      source: 'api',
      metier: fromApi,
      familyLabel: getCommerceFamily(familyId)?.label || familyId,
      actif: fromApi.actif,
      deprecated: fromApi.deprecated,
      archivedAt: fromApi.archivedAt,
      system: fromApi.system,
    };
  }

  const staticM = getCommerceMetier(id);
  if (staticM) {
    return {
      id,
      label: staticM.label,
      icon: staticM.icon || '📦',
      familyId: staticM.family,
      badge: staticM.badge || null,
      source: 'static',
      metier: staticM,
      familyLabel: getCommerceFamily(staticM.family)?.label || staticM.family,
    };
  }

  return {
    id,
    label: humanizeMetierId(id),
    icon: '📦',
    familyId: 'autre',
    badge: null,
    source: 'humanize',
    metier: null,
    familyLabel: getCommerceFamily('autre')?.label || 'Autre',
  };
}

/** Libellé seul (sans icône), jamais code brut. */
export function resolveMetierLabel(code, options = {}) {
  return resolveMetierDisplay(code, options).label;
}
